import { type ClientConfig, clientConfigSchema } from '@/types';

/**
 * Read a whole client config from an uploaded JSON file. The check is strict:
 * a setting the portal does not know is refused, the same as a full replace.
 */
export async function readConfigFile(file: File): Promise<ClientConfig> {
  const text = await file.text();
  const parsed = JSON.parse(text);
  return clientConfigSchema.strict().parse(parsed) as ClientConfig;
}

interface SchemaIssue {
  path: (string | number)[];
  message: string;
}

/**
 * What the config rules refused, one item for each fault: "region: Required".
 * Null when the error did not come from the rules.
 */
export function schemaIssues(err: unknown): string | null {
  const issues = (err as { issues?: SchemaIssue[] } | null)?.issues;
  if (!Array.isArray(issues) || issues.length === 0) return null;
  return issues.map((issue) => `${issue.path.join('.') || 'config'}: ${issue.message}`).join('; ');
}

/** Why an uploaded config file was refused, in words a person can act on. */
export function describeConfigFileError(err: unknown): string {
  const issues = schemaIssues(err);
  if (issues) {
    return `The file does not match the config rules. ${issues}.`;
  }
  if (err instanceof SyntaxError) {
    return `The file is not valid JSON. ${err.message}.`;
  }
  return err instanceof Error && err.message ? err.message : 'The file could not be read.';
}

/** A client's config as a JSON file named after the client, ready to hand to the download helper. */
export function configFileFor(clientName: string, config: ClientConfig) {
  const content = JSON.stringify(config, null, 2);
  return {
    name: `${clientName}.json`,
    content,
    mimeType: 'application/json',
    size: new Blob([content]).size,
  };
}
